import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Sparkles } from "lucide-react";

export function PointsLedger({ entityType, entityId, title = "How this level was earned" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!entityId) return;
    http.get("/point-events", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setData(r.data))
      .catch(() => setData({ events: [], total: 0 }));
  }, [entityType, entityId]);

  if (!data) return null;
  if (data.events.length === 0) {
    return (
      <Card data-testid={`points-ledger-${entityType}`}>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" /> {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-500 italic">No point-earning activity recorded yet.</div>
        </CardContent>
      </Card>
    );
  }

  // Normalize reason: strip "Term N:" prefix and " — details" suffix so
  // "Term 1: Well hydrated — 7 glasses/day" and "Term 2: Well hydrated — 8 glasses/day"
  // collapse into a single "Well hydrated" row.
  const normalize = (reason) => {
    if (!reason) return "Other";
    let r = String(reason).replace(/^Term\s*\d+:\s*/i, "");
    const dashIdx = r.indexOf(" — ");
    if (dashIdx >= 0) r = r.slice(0, dashIdx);
    return r.trim();
  };

  // Aggregate by normalized reason
  const byReason = {};
  data.events.forEach((e) => {
    const key = normalize(e.reason);
    byReason[key] = (byReason[key] || 0) + (e.delta || 0);
  });
  const aggregated = Object.entries(byReason)
    .map(([reason, total]) => ({ reason, total }))
    .sort((a, b) => b.total - a.total);

  return (
    <Card data-testid={`points-ledger-${entityType}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" /> {title}
          </CardTitle>
          <Badge className="bg-sky-500 text-white" data-testid={`ledger-total-${entityType}`}>
            <Sparkles className="w-3 h-3 mr-1" /> {data.total.toLocaleString()} pts
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {aggregated.map((row, i) => (
            <div key={row.reason} data-testid={`ledger-row-${entityType}-${i}`} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2">
              <div className="text-sm text-slate-700">{row.reason}</div>
              <div className="tabular-nums font-mono font-semibold text-emerald-600">+{row.total.toLocaleString()}</div>
            </div>
          ))}
        </div>
        {data.events.length > 0 && (
          <div className="mt-3 text-[11px] uppercase tracking-widest text-slate-400 font-bold">Recent events</div>
        )}
        <div className="mt-2 space-y-1">
          {data.events.slice(0, 5).map((e) => (
            <div key={e.id} className="flex items-center justify-between text-xs text-slate-500">
              <span className="truncate">{e.reason}</span>
              <span className="tabular-nums text-slate-400 shrink-0 ml-3">{e.at?.slice(0, 10) || ""}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
